import { useEffect, useState } from "react";
import { v4 as uuidv4 } from "uuid";

import Icon from "./Icon";
import Message from "./Message";

export const TextArea = ({
  placeholder,
  class: c,
  className: cn,
  readOnly,
  id,
  onChange,
  value,
  rows,
  name,
  required,
}) => {
  const [className, setClassName] = useState(
    "appearance-none bg-white border border-solid border-zinc-300 resize-none rounded-lg placeholder-zinc-400 font-normal"
  );

  useEffect(() => {
    if (cn) {
      setClassName(cn);
    } else if (c) {
      const tmpSet = new Set(`${className} ${c}`.split(" "));
      setClassName([...tmpSet].join(" "));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [c, cn]);

  return (
    <textarea
      {...{
        className,
        id,
        name,
        onChange,
        placeholder,
        readOnly: readOnly === undefined ? false : readOnly,
        rows,
        value,
        required,
      }}
    />
  );
};

export const TextInput = ({
  class: c,
  className: cn,
  id,
  onChange,
  onBlur,
  placeholder,
  readOnly,
  value,
  defaultValue,
  required,
}) => {
  const [className, setClassName] = useState(
    "appearance-none bg-white border border-zinc-300 placeholder-zinc-400 rounded-lg shadow-sm text-sm"
  );

  useEffect(() => {
    if (cn) {
      setClassName(cn);
    } else if (c) {
      const tmpSet = new Set(`${className} ${c}`.split(" "));
      setClassName([...tmpSet].join(" "));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [c, cn]);

  return (
    <>
      <input
        type="text"
        {...{
          className,
          id,
          onBlur,
          onChange,
          placeholder,
          readOnly,
          value,
          defaultValue,
          required,
        }}
      />
    </>
  );
};

export const DateInput = ({ id, onChange, value }) => {
  return (
    <input
      type="date"
      {...{
        className:
          "appearance-none bg-white border border-zinc-300 placeholder-zinc-400 rounded-lg shadow-sm text-sm p-2",
        id,
        onChange,
        value,
      }}
    />
  );
};

export const IconButton = ({
  class: c,
  className: cn,
  children,
  disabled,
  onClick,
}) => {
  const [className, setClassName] = useState(
    "btn btn-ghost btn-sm p-0 w-8 disabled:bg-transparent"
  );

  useEffect(() => {
    if (cn) {
      setClassName(cn);
    } else if (c) {
      const tmpSet = new Set(`${className} ${c}`.split(" "));
      setClassName([...tmpSet].join(" "));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [c, cn]);

  return (
    <button className="" {...{ disabled, onClick }}>
      {children}
    </button>
  );
};

export const ButtonGroup = ({ children, label }) => {
  return (
    <div className="inline-flex gap-2 items-center">
      {label && <p className="text-sm font-bold">{label}</p>}
      <div className="btn-group w-full bg-white rounded-lg">{children}</div>
    </div>
  );
};

export const ButtonGroupItem = ({
  active,
  children,
  disabled,
  id,
  onClick,
}) => {
  const [className, setClassName] = useState(
    "w-auto btn btn-sm btn-primary flex-1"
  );

  useEffect(() => {
    const tmpSet = new Set(`${className}`.split(" "));
    const tmpArr = [...tmpSet]
      .filter((v) => v !== "btn-active")
      .filter((v) => v !== "btn-outline");
    if (active) {
      setClassName([...tmpArr, "btn-active"].join(" "));
    } else {
      setClassName([...tmpArr, "btn-outline"].join(" "));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  return <button {...{ className, disabled, id, onClick }}>{children}</button>;
};

export const LinkButton = ({ children, onClick }) => {
  return (
    <button
      className="btn btn-link btn-sm self-start p-0 text-left normal-case"
      onClick={onClick}
    >
      {children}
    </button>
  );
};

export const SaveButton = ({ children, disabled, onClick }) => {
  return (
    <button
      {...{
        className: "btn btn-block btn-accent border-none",
        disabled,
        type: "submit",
        onClick,
      }}
    >
      {children}
    </button>
  );
};

export const GhostButton = ({ children, onClick }) => (
  <button className="btn btn-ghost btn-sm p-0 w-8" onClick={onClick}>
    {children}
  </button>
);

export const Button = ({
  children,
  className: cn,
  class: c,
  id,
  type,
  onClick,
  outlined,
  active,
  disabled,
  isGlass,
  isLoading,
  noAni,
  size,
  wide,
  block,
  circle,
  square,
}) => {
  const [className, setClassName] = useState("");

  useEffect(() => {
    const tmpSet = new Set(
      `btn btn-primary ${cn} ${c}`.split(" ").filter((v) => v !== "undefined")
    );
    const styles = [
      "btn-primary",
      "btn-secondary",
      "btn-accent",
      "btn-info",
      "btn-success",
      "btn-warning",
      "btn-error",
      "btn-ghost",
      "btn-link",
    ];
    const sizes = ["btn-lg", "btn-md", "btn-sm", "btn-xs"];
    const etcs = [
      "glass",
      "loading",
      "no-animation",
      "btn-wide",
      "btn-block",
      "btn-circle",
      "btn-square",
    ];
    const tmpArr = [...tmpSet]
      .filter((v) => !styles.includes(v))
      .filter((v) => !sizes.includes(v))
      .filter((v) => !etcs.includes(v));
    if (styles && styles.includes(`btn-${type}`)) {
      tmpArr.push(`btn-${type}`);
    }
    if (typeof disabled === "boolean" && disabled) {
      tmpArr.push("btn-disabled");
    } else {
      if (typeof outlined === "boolean" && outlined) {
        tmpArr.push("btn-outline");
      }
      if (typeof active === "boolean" && active) {
        tmpArr.push("btn-active");
      }
    }
    if (typeof isGlass === "boolean" && isGlass) {
      tmpArr.push("glass");
    }
    if (typeof isLoading === "boolean" && isLoading) {
      tmpArr.push("loading");
    }
    if (typeof noAni === "boolean" && noAni) {
      tmpArr.push("no-animation");
    }
    if (typeof wide === "boolean" && wide) {
      tmpArr.push("btn-wide");
    }
    if (typeof block === "boolean" && block) {
      tmpArr.push("btn-block");
    }
    if (typeof circle === "boolean" && circle) {
      tmpArr.push("btn-circle");
    }
    if (typeof square === "boolean" && square) {
      tmpArr.push("btn-square");
    }
    if (size && sizes.includes(`btn-${size}`)) {
      tmpArr.push(`btn-${size}`);
    }
    setClassName(tmpArr.join(" "));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    cn,
    type,
    outlined,
    active,
    disabled,
    isGlass,
    isLoading,
    noAni,
    size,
    wide,
    block,
    circle,
    square,
  ]);

  return (
    <button
      {...{
        className,
        id,
        onClick,
      }}
    >
      {children}
    </button>
  );
};

export const TimeSelect = ({ className, type, value: val, ...rest }) => {
  // type ['hour', 'min', 'sec']
  const l = type === "hour" ? 24 : 60;
  const unit = type === "hour" ? "시간" : type === "min" ? "분" : "초";
  const id = rest.id || `${type}`;
  return (
    <select
      {...{
        ...rest,
        id,
        value: val.toString() || "",
        className: `rounded-lg border border-zinc-300 w-auto p-2 bg-white shadow-sm text-sm ${className}`,
      }}
    >
      {Array(l + 1)
        .fill(0)
        .map((_v, j) => (j === 0 ? unit : j - 1))
        .map((v, j) => (
          <option key={`${id}_${j}`} value={j === 0 ? "" : v}>
            {v < 10 ? `0${v}` : v}
          </option>
        ))}
    </select>
  );
};

export const OptionText = ({ placeholder, onChange, readOnly, value }) => {
  return (
    <TextInput
      {...{
        class: "p-2 min-w-fit",
        placeholder: placeholder || "placeholder를 입력하세요.",
        type: "text",
        onChange,
        readOnly,
        value,
      }}
    />
  );
};

export const OptionTextArea = ({ onChange, readOnly, value }) => (
  <TextArea
    {...{
      placeholder: "placeholder를 입력하세요.",
      class: "flex-1 w-full p-2 shadow-sm text-sm",
      onChange,
      readOnly,
      value,
    }}
  />
);

export const OptionRating = () => (
  <>
    <p className="text-xs text-red-300 mb-[-1rem]">
      {"> 퀴즈 응답시, 아래와 같이 보여집니다."}
    </p>
    <div className="rating gap-1">
      {new Array(10).fill(null).map((_v, i) => (
        <input
          type="radio"
          className="mask mask-star-2 border-orange-600 bg-yellow-400"
          key={`rating__${i}`}
          checked={i === 4}
          readOnly
        />
      ))}
    </div>
  </>
);

export const OptionRadio = ({
  name,
  list,
  readOnly,
  error,
  onChange,
  onRemove,
  onAdd,
}) => {
  const [addedInput, setAddedInput] = useState(false);
  const handleAdd =
    (bInput = false) =>
    () => {
      if (bInput) {
        setAddedInput(!addedInput);
      }
      const content = bInput ? { type: "text", content: "" } : "";
      onAdd(content);
    };

  const handleRemove = (idx) => () => {
    const newOne = [...list];
    const [rmvOne] = newOne.splice(idx, 1);
    if (typeof rmvOne === "object") {
      setAddedInput(false);
    }
    onRemove(newOne);
  };

  return (
    <div className="flex flex-col w-full gap-1">
      {list.map(({ content }, j) => (
        <div
          className="inline-flex w-full gap-2 items-center"
          key={`${name}${j}`}
        >
          <input
            type="radio"
            className="h-4 w-4 border-zinc-400 bg-white"
            checked={j === 0}
            readOnly
          />
          <TextInput
            className={`w-full px-2 py-1 ${
              typeof content === "string" ? "" : "bg-zinc-300 placeholder-white"
            }`}
            onChange={onChange(j)}
            readOnly={readOnly}
            type="text"
            value={typeof content === "string" ? content : content.content}
          />
          <GhostButton onClick={handleRemove(j)}>
            <Icon name="x-circle" className="text-zinc-600" />
          </GhostButton>
        </div>
      ))}
      {!readOnly && (
        <div className="flex flex-col mt-2">
          <LinkButton onClick={handleAdd()}>
            <Icon name="plus" size="sm" />
            보기 추가
          </LinkButton>
          {!addedInput && (
            <LinkButton onClick={handleAdd(true)}>
              <Icon name="plus" size="sm" />
              직접 입력 보기 추가
            </LinkButton>
          )}
        </div>
      )}
      {!!error && <Message type="error">{error}</Message>}
    </div>
  );
};

export const OptionSelect = ({
  type,
  name,
  list,
  answers,
  readOnly,
  error,
  onChange,
  onRemove,
  onAdd,
  onAnswer,
}) => {
  const [addedInput, setAddedInput] = useState(false);
  const handleAdd =
    (bInput = false) =>
    () => {
      if (bInput) {
        setAddedInput(true);
      }
      const content = bInput
        ? { content: { type: "text", content: "" } }
        : { content: "" };
      const newOne = [...list];
      if (!bInput && addedInput && newOne.length > 2) {
        newOne.splice(newOne.length - 2, 0, content);
      } else {
        newOne.push(content);
      }
      onAdd(newOne);
    };

  const handleRemove = (idx) => () => {
    const newOne = [...list];
    const [rmvOne] = newOne.splice(idx, 1);
    if (typeof rmvOne === "object") {
      setAddedInput(false);
    }
    onRemove(newOne);
  };

  /* const handleChange =
    (idx, bSub = false) =>
    (e) => {
      const newOne = [...list];
      console.log(newOne, e.target.value, idx);
      const obj = newOne[idx];
      if (bSub) {
        if (typeof obj.content === "string") {
          const content = obj.content;
          newOne.splice(idx, 1, { content: { type: "text", content } });
        } else {
          newOne.splice(idx, 1, { content: obj.content.content });
        }
      } else {
        if (typeof obj.content === "string") {
          newOne.splice(idx, 1, { content: e.target.value });
        } else {
          newOne.splice(idx, 1, {
            content: { type: "text", content: e.target.value },
          });
        }
      }
      onChange(newOne);
    }; */

  const handleSelect = (content) => () => {
    let newOne = [];
    if (answers.includes(content)) {
      newOne = answers.filter((v) => v !== content);
    } else {
      if (type === "radio") {
        newOne = [content];
      } else {
        newOne = [...answers, content];
      }
    }
    onAnswer(newOne);
  };

  const handleBlur =
    (idx, bSub = false) =>
    (e) => {
      const newOne = [...list];
      const obj = newOne[idx];
      if (bSub) {
        if (typeof obj.content === "string") {
          const content = obj.content;
          newOne.splice(idx, 1, { content: { type: "text", content } });
        } else {
          newOne.splice(idx, 1, { content: obj.content.content });
        }
      } else {
        const content = e.target.value.trimStart().trimEnd();
        if (typeof obj.content === "string") {
          newOne.splice(idx, 1, { content });
        } else {
          newOne.splice(idx, 1, { content: { type: "text", content } });
        }
      }
      onChange(newOne);
    };
  return (
    <div className="flex flex-col gap-1">
      {list.map(({ content }, j) => {
        return (
          <div
            className="inline-flex w-full gap-2 items-center"
            key={`${name}${j}_${uuidv4()}`}
          >
            <input
              name={name}
              type={type}
              className="h-4 w-4 border-zinc-400 bg-white"
              defaultChecked={readOnly ? j === 0 : answers.includes(content)}
              onClick={handleSelect(content)}
              // readOnly
            />
            <TextInput
              className={`px-2 py-1 flex-auto ${
                typeof content === "string"
                  ? ""
                  : "bg-zinc-300 placeholder-white"
              }`}
              onBlur={handleBlur(j)}
              readOnly={readOnly}
              defaultValue={
                typeof content === "string" ? content : content.content
              }
            />
            <GhostButton onClick={handleRemove(j)}>
              <Icon name="x-circle" className="text-zinc-600" />
            </GhostButton>
          </div>
        );
      })}
      {!readOnly && (
        <div className="flex flex-col mt-2">
          <LinkButton onClick={handleAdd()}>
            <Icon name="plus" size="sm" />
            보기 추가
          </LinkButton>
          {!addedInput && (
            <LinkButton onClick={handleAdd(true)}>
              <Icon name="plus" size="sm" />
              직접 입력 보기 추가
            </LinkButton>
          )}
        </div>
      )}
      {!!error && <Message type="error">{error}</Message>}
    </div>
  );
};

export const Alert = ({ children }) => {
  return (
    <div className="inline-flex shadow-sm p-2 rounded-md items-center bg-primary border border-primary text-primary-content bg-opacity-10">
      <div className="inline-flex gap-2 items-center">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="stroke-current flex-shrink-0 h-6 w-6"
          fill="none"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M4.5 12.75l6 6 9-13.5"
          />
        </svg>
        <span className="text-sm">{children}</span>
      </div>
    </div>
  );
};

export const SuccessAlert = ({ children }) => {
  return (
    <div className="inline-flex shadow-sm p-2 rounded-md items-center bg-success border border-success text-success-content bg-opacity-10">
      <div className="inline-flex gap-2 items-center">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke-width="1.5"
          stroke="currentColor"
          className="w-6 h-6"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
        <span className="text-sm">{children}</span>
      </div>
    </div>
  );
};
export const ErrorAlert = ({ children }) => {
  return (
    <div className="inline-flex shadow-sm p-2 rounded-md items-center bg-red-500 border border-red-500 text-red-500 bg-opacity-10">
      <div className="inline-flex gap-2 items-center">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke-width="1.5"
          stroke="currentColor"
          className="w-6 h-6"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M9.75 9.75l4.5 4.5m0-4.5l-4.5 4.5M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>

        <span className="text-sm">{children}</span>
      </div>
    </div>
  );
};

export const Rating = ({ length, value, onChange, id, name, readOnly }) => {
  const handleChange = (e) => {
    if (readOnly) return;
    const obj = {
      ...e,
      target: {
        id: e.currentTarget.id,
        type: "rating",
        name,
        value: Number(e.currentTarget.id.split("-")[2]) + 1,
      },
    };
    onChange(obj);
  };
  return (
    <div className="rating gap-1">
      {new Array(length).fill(null).map((_v, i) =>
        i < value ? (
          <div id={`${id}-${i}`} key={`${id}_${i}`} onClick={handleChange}>
            <svg
              aria-hidden="true"
              className="w-8 h-8 text-yellow-400"
              fill="currentColor"
              viewBox="0 0 20 20"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"></path>
            </svg>
          </div>
        ) : (
          <div id={`${id}-${i}`} key={`${id}_${i}`} onClick={handleChange}>
            <svg
              aria-hidden="true"
              className="w-8 h-8 text-yellow-400"
              stroke="currentColor"
              fill="white"
              viewBox="0 0 20 20"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"></path>
            </svg>
          </div>
        )
      )}
    </div>
  );
};
